import { useAuthorsQuery } from "@pathwright/graphql-client/src/generated"
import PropTypes from "prop-types"
import { useCallback, useMemo, useState } from "react"
import { withPathwrightContext } from "../../pathwright/PathwrightContext"
import { flattenEdges } from "../../utils/apollo"
import useControlledBrowseQueryParams from "../../utils/useControlledBrowseQueryParams"
import Browse from "./Browse"

/**
 * Browse roots that should re-sync filters from the URL when entered
 * (cover/modal stays mounted underneath).
 */
function getStoreBrowseIsRootPath(schoolId) {
  return (pathname) => {
    if (schoolId) {
      const base = `/library/store/${schoolId}`
      return pathname === base || pathname === `${base}/`
    }
    return (
      pathname === "/store/" ||
      pathname === "/store" ||
      pathname === "/library/" ||
      pathname === "/library"
    )
  }
}

function BrowseContainer({
  children,
  schoolId,
  browseTo,
  structure,
  licensingMode,
  offerKinds,
  context
}) {
  const scopedSchoolId = schoolId || context.school.id

  const isRootPath = useMemo(
    () => getStoreBrowseIsRootPath(schoolId),
    [schoolId]
  )

  const {
    debouncedSearch,
    handleDebounceSearch,
    selectedAuthorNames,
    setSelectedAuthorNames,
    selectedCategoryNames,
    setSelectedCategoryNames
  } = useControlledBrowseQueryParams({ isRootPath })

  // Publisher is Store-local (not in the URL).
  const [publisherIdFilter, setPublisherIdFilter] = useState(null)

  const authorsQuery = useAuthorsQuery({
    variables: {
      school_id: scopedSchoolId,
      first: 50
    },
    skip: !scopedSchoolId
  })
  const authors = useMemo(
    () => flattenEdges(authorsQuery.data?.context?.school?.authors) || [],
    [authorsQuery.data]
  )

  // URL / cover pills / Library use category *names* (`?category=Science`).
  // Store GraphQL still filters by *ids* (`category_ids: { eq }`). Build a
  // name→id map from browse `structure`, where each section heading is the
  // category name and `queryOptions.categoryIdFilter` holds its id.
  const categoryIdByName = useMemo(() => {
    const map = new Map()
    for (const item of structure) {
      const id = item.queryOptions?.categoryIdFilter?.eq
      if (item.heading != null && id != null) {
        map.set(item.heading, id)
      }
    }
    return map
  }, [structure])

  // Same bridge for authors: URL uses `?author=Full Name`, Store query uses
  // `author_ids`. Facet dropdowns select by id; we reverse-map to a name when
  // writing the URL, and name→id here when reading it.
  const authorIdByName = useMemo(() => {
    const map = new Map()
    for (const author of authors) {
      if (author?.full_name != null && author?.id != null) {
        map.set(author.full_name, author.id)
      }
    }
    return map
  }, [authors])

  const activeFacets = useMemo(() => {
    const categoryName = selectedCategoryNames[0]
    const authorName = selectedAuthorNames[0]
    const categoryId =
      categoryName != null ? categoryIdByName.get(categoryName) : undefined
    const authorId =
      authorName != null ? authorIdByName.get(authorName) : undefined

    const facets = {}
    if (categoryId != null) {
      facets.categoryIdFilter = { eq: categoryId }
    }
    if (authorId != null) {
      facets.authorIdFilter = { eq: authorId }
    }
    if (publisherIdFilter != null) {
      facets.publisherIdFilter = publisherIdFilter
    }
    if (debouncedSearch) {
      facets.search = debouncedSearch
    }

    // Filtered UI ("Back to Browse") when any facet is active.
    // Category/author names in the URL count even before id lookup resolves,
    // so the filtered view shows immediately on pill navigation.
    const hasUrlFacet =
      selectedCategoryNames.length > 0 || selectedAuthorNames.length > 0
    if (
      !hasUrlFacet &&
      !facets.publisherIdFilter &&
      !facets.search &&
      !facets.categoryIdFilter &&
      !facets.authorIdFilter
    ) {
      return null
    }
    return facets
  }, [
    selectedCategoryNames,
    selectedAuthorNames,
    categoryIdByName,
    authorIdByName,
    publisherIdFilter,
    debouncedSearch
  ])

  const handleChangeFacets = useCallback(
    (nextFacets) => {
      if (!nextFacets) {
        setSelectedCategoryNames([])
        setSelectedAuthorNames([])
        handleDebounceSearch(null)
        setPublisherIdFilter(null)
        return
      }

      // Facet menus replace the active filter set. Carry search only when the
      // caller included it (e.g. clearing search while keeping a category);
      // otherwise drop it so picking a facet doesn't leave a stale query.
      if ("search" in nextFacets) {
        handleDebounceSearch(nextFacets.search || null)
      } else {
        handleDebounceSearch(null)
      }

      if (nextFacets.categoryIdFilter?.eq != null) {
        const id = nextFacets.categoryIdFilter.eq
        // Reverse of categoryIdByName: facet UI / "View all" pass an id;
        // write the matching section heading so the URL stays name-based.
        const name = structure.find(
          (item) => item.queryOptions?.categoryIdFilter?.eq === id
        )?.heading
        setSelectedCategoryNames(name ? [name] : [])
        setSelectedAuthorNames([])
        setPublisherIdFilter(null)
        return
      }

      if (nextFacets.authorIdFilter?.eq != null) {
        const id = nextFacets.authorIdFilter.eq
        const name = authors.find((a) => a.id === id)?.full_name
        setSelectedAuthorNames(name ? [name] : [])
        setSelectedCategoryNames([])
        setPublisherIdFilter(null)
        return
      }

      if (nextFacets.publisherIdFilter != null) {
        setPublisherIdFilter(nextFacets.publisherIdFilter)
        setSelectedCategoryNames([])
        setSelectedAuthorNames([])
        return
      }

      // Enter-to-search replaces facets with `{ search }` only.
      if ("search" in nextFacets) {
        setSelectedCategoryNames([])
        setSelectedAuthorNames([])
        setPublisherIdFilter(null)
      }
    },
    [
      structure,
      authors,
      setSelectedCategoryNames,
      setSelectedAuthorNames,
      handleDebounceSearch
    ]
  )

  return (
    <Browse
      schoolId={schoolId}
      browseTo={browseTo}
      structure={structure}
      activeFacets={activeFacets}
      onChangeFacets={handleChangeFacets}
      hideBannerAction={!!schoolId} // hide banner action button if scoped to another school
      resourcesQueryOptions={{
        fetchPolicy: "cache-and-network",
        includeSchoolLicense: !!schoolId, // load own school licenses if scoped to another school
        licenseeSchoolId: context.school.id,
        schoolId: schoolId, // if set, scopes resources to another school
        licensingMode: licensingMode,
        offerKinds: offerKinds
      }}
    >
      {children}
    </Browse>
  )
}

BrowseContainer.displayName = "BrowseContainer"

BrowseContainer.propTypes = {
  // optional, render something additional (like a back button)
  children: PropTypes.any,
  // block strucutre
  structure: PropTypes.array.isRequired,
  // configure resource link
  browseTo: PropTypes.func,
  // scope queries to another school
  schoolId: PropTypes.number,
  // filter by license offering type
  licensingMode: PropTypes.string,
  // filter to resources with matching store Offer kinds
  offerKinds: PropTypes.object,
  context: PropTypes.object
}

export default withPathwrightContext(BrowseContainer)
